<template>
  <div>
    <h4 class="table-page-header">
      Admin
      <el-tooltip content="Add New Admin">
        <el-button type="text" @click="createAdmin">
          <i class="fa-solid fa-circle-plus" style="font-size: 22px"></i>
        </el-button>
      </el-tooltip>
    </h4>
    <div class="filter-section">
      <div class="filter-row">
        <div class="filter-group search-group">
          <el-input
            v-model="searchQuery"
            placeholder="Search"
            clearable
            @clear="clearSearch"
            @input="onSearch"
            class="search-input"
          >
            <i
              slot="prepend"
              class="fa fa-search"
              style="padding-left: 4px; padding-right: 4px"
            ></i>
          </el-input>
        </div>
        <div class="filter-group">
          <AIVSelect
            v-model="activeStatusFilter"
            :options="activeStatusOptions"
            placeholder="Filter by status"
            @change="onStatusFilterChange"
            class="compact-filter"
          />
        </div>
      </div>
    </div>
    <AIVTable
      :columns="adminColumns"
      :data="admins"
      :pagination="pagination"
      :item-name="'admin users'"
      :row-clickable="true"
      @pageChange="fetchAdmins"
      @pageSizeChange="handlePageSizeChange"
      @sort-change="handleSortChange"
      @row-click="handleRowClick"
    >
      <template #cell-actions="{ row }">
        <button
          v-if="isAdmin && row.active"
          class="btn btn-sm btn-primary mr-2"
          @click.stop="openAdminPasswordModal(row)"
          title="Change Password"
        >
          <i class="fa-solid fa-key"></i>
        </button>
        <button
          :class="row.active ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-success'"
          @click="$confirmAction(() => toggleAdminStatus(row), row.active ? `Are you sure you want to deactivate ${row.first_name} ${row.last_name}? They will no longer be able to login to the Oppo app.` : `Are you sure you want to activate ${row.first_name} ${row.last_name}? They will be able to login to the Oppo app again.`)"
          :title="row.active ? 'Deactivate' : 'Activate'"
        >
          <i :class="row.active ? 'fa-solid fa-user-slash' : 'fa-solid fa-user-check'"></i>
        </button>
      </template>
    </AIVTable>
    <AIVDialog
      v-model="$modal.state.create_or_edit_admin"
      :title="adminBeingEdited ? 'Edit Admin' : 'Create a New Admin'"
      width="90%"
      height="85vh"
    >
      <div class="admin-form-container">
        <div class="row col-md-12 d-flex justify-content-between">
          <el-form
            ref="createForm"
            :model="admin"
            label-position="top"
            label-width="120px"
            autocomplete="off"
          >
          <AIVRow
            :record.sync="admin"
            :fields="[
              { label: 'First Name', key: 'first_name' },
              { label: 'Last Name', key: 'last_name' },
              { label: 'Email', key: 'email', autocomplete: 'off' },
            ]"
          />
          <AIVRow
            :record.sync="admin.address"
            :fields="[
              { label: 'Address1', key: 'address1' },
              { label: 'Address2', key: 'address2' },
            ]"
          />
          <AIVRow
            :record.sync="admin.address"
            :fields="[
              { label: 'City', key: 'city' },
              { label: 'State', key: 'state' },
              { label: 'Zip', key: 'zip' },
            ]"
          />
          <AIVRow
            :record.sync="admin"
            :fields="[
              { label: 'Mobile Phone', key: 'mobile_phone' },
              { label: 'Other Phone', key: 'other_phone' },
            ]"
          />
          <AIVRow
            :record.sync="admin"
            :fields="[
              {
                label: 'Business Unit',
                key: 'business_unit',
                type: 'select',
                options: businessUnitOptions,
                disabled: true,
              },
              { label: 'Position ID', key: 'position_id' },
            ]"
          />
          <div class="row mb-3 w-100 mx-0">
            <div class="col-md-6 px-2">
              <label class="d-block">Title</label>
              <el-input v-model="admin.title" placeholder="Enter title"></el-input>
            </div>
            <div class="col-md-6 px-2">
              <label class="d-block">Managed Offices</label>
              <AIVSelect
                v-model="admin.office_ids"
                :options="officeOptions"
                :multiSelect="true"
                placeholder="Select offices"
              />
            </div>
          </div>
          <div class="row mb-3 w-100 mx-0" v-if="!adminBeingEdited">
            <div class="col-md-4 px-2">
              <label class="d-block">Password</label>
              <div class="password-input-wrapper">
                <el-input
                  v-model="admin.password"
                  :type="passwordVisible ? 'text' : 'password'"
                  placeholder="Enter password"
                  autocomplete="new-password"
                  class="w-100"
                ></el-input>
                <i
                  :class="
                    passwordVisible
                      ? 'fa-solid fa-eye-slash'
                      : 'fa-solid fa-eye'
                  "
                  @click="togglePasswordVisibility"
                  class="password-toggle-icon"
                  aria-hidden="true"
                ></i>
              </div>
            </div>
            <div class="col-md-4 px-2">
              <label class="d-block">Confirm Password</label>
              <div class="password-input-wrapper">
                <el-input
                  v-model="admin.confirmPassword"
                  :type="confirmPasswordVisible ? 'text' : 'password'"
                  placeholder="Confirm password"
                  autocomplete="new-password"
                  class="w-100"
                ></el-input>
                <i
                  :class="
                    confirmPasswordVisible
                      ? 'fa-solid fa-eye-slash'
                      : 'fa-solid fa-eye'
                  "
                  @click="toggleConfirmPasswordVisibility"
                  class="password-toggle-icon"
                  aria-hidden="true"
                ></i>
              </div>
              <span class="w-100 d-block">
                <span
                  v-if="passwordError"
                  class="text-danger"
                  style="font-size: 13px"
                  >{{ passwordError }}</span
                >
              </span>
            </div>
            <div class="col-md-4 px-2">
              <label class="d-block">Password Requirements</label>
              <div class="password-requirements">
                <ul>
                  <li>At least 8 characters</li>
                  <li>At least one number</li>
                  <li>At least one special character</li>
                </ul>
              </div>
            </div>
          </div>
          </el-form>
        </div>
      </div>
      <template slot="footer">
        <div class="text-center">
          <el-button type="primary" round @click="saveAdmin">{{
            adminBeingEdited ? "Update" : "Create"
          }}</el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Admin Password Change Modal -->
    <ChangePasswordModal
      v-model="showAdminPasswordModal"
      :is-admin="true"
      subject-label="Admin"
      :caregiver-id="adminForPasswordChange?.id"
      :caregiver-name="adminForPasswordChange ? `${adminForPasswordChange.first_name} ${adminForPasswordChange.last_name}` : ''"
      @success="handleAdminPasswordSuccess"
    />
  </div>
</template>

<script>
import { adminUsersService } from "@/serviceHandlers/adminUsersService";
import { officesService } from "@/serviceHandlers/officesService";
import ChangePasswordModal from "@/components/ChangePasswordModal.vue";
import { useLoginStore } from "@/stores/loginStore";

export default {
  name: "Admin",
  components: {
    ChangePasswordModal,
  },
  data() {
    return {
      passwordError: "",
      showAdminPasswordModal: false,
      adminForPasswordChange: null,
      passwordVisible: false,
      confirmPasswordVisible: false,
      adminBeingEdited: null,
      adminColumns: [
        { key: "first_name", label: "First Name" },
        { key: "last_name", label: "Last Name" },
        { key: "email", label: "Email", class: "hide-on-medium" },
        { key: "mobile_phone", label: "Phone", class: "hide-on-small" },
        { key: "title", label: "Title" },
        { key: "actions", label: "Actions", sortable: false },
      ],
      newAdmin: {
        first_name: "",
        last_name: "",
        email: "",
        mobile_phone: "",
        other_phone: "",
        password: "",
        confirmPassword: "",
        title: "",
        business_unit: "MNG - Management",
        position_id: null,
        office_ids: [],
        address: {
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
        },
      },
      searchQuery: "",
      activeStatusFilter: "active",
      activeStatusOptions: [
        { id: "active", name: "Active" },
        { id: "inactive", name: "Inactive" },
      ],
      sortProp: "",
      sortOrder: "",
      admins: [],
      officeOptions: [],
      businessUnitOptions: [
        { id: "MNG - Management", name: "MNG - Management" },
      ],
      pagination: {
        total: 0,
        perPage: 20,
        currentPage: 1,
        totalPages: 0,
      },
      currentStep: 1,
    };
  },
  computed: {
    isAdmin() {
      const loginStore = useLoginStore();
      const role = loginStore.user?.user_type || "";
      return role === "Admin" || role === "Super Admin";
    },
    admin: {
      get() {
        return this.adminBeingEdited || this.newAdmin;
      },
      set(val) {
        if (this.adminBeingEdited) {
          this.adminBeingEdited = val;
        } else {
          this.newAdmin = val;
        }
      },
    },
  },
  async mounted() {
    await Promise.all([this.fetchAdmins(), this.getOffices()]);
  },
  methods: {
    openAdminPasswordModal(admin) {
      this.adminForPasswordChange = admin;
      this.showAdminPasswordModal = true;
    },
    closeAdminPasswordModal() {
      this.showAdminPasswordModal = false;
      this.adminForPasswordChange = null;
    },
    handleAdminPasswordSuccess() {
      this.$notifySuccess("Password changed successfully!");
      this.closeAdminPasswordModal();
    },
    handleRowClick(admin) {
      this.$router.push({
        name: "AdminDetails",
        params: { id: admin.id },
        query: { name: `${admin.first_name} ${admin.last_name}` },
      });
    },
    async fetchAdmins(pageNumber = 1) {
      this.$spinner.show();
      try {
        const params = new URLSearchParams();
        params.append("page", pageNumber);
        params.append("perPage", this.pagination.perPage);
        params.append("activeStatus", this.activeStatusFilter);

        if (this.searchQuery) {
          params.append("searchTerm", this.searchQuery);
        }

        if (this.sortProp) {
          params.append("sortField", this.sortProp);
          params.append("sortOrder", this.sortOrder);
        }

        const response = await adminUsersService.listPaginated(params);
        this.admins = response.rows;
        this.pagination = response.pagination;
      } catch (error) {
        this.$notifyError("Could not load admins: " + error);
      } finally {
        this.$spinner.hide();
      }
    },
    onSearch() {
      this.pagination.currentPage = 1;
      this.fetchAdmins(1);
    },
    clearSearch() {
      this.searchQuery = "";
      this.pagination.currentPage = 1;
      this.fetchAdmins(1);
    },
    onStatusFilterChange() {
      this.pagination.currentPage = 1;
      this.fetchAdmins(1);
    },
    handleSortChange({ prop, order }) {
      this.sortProp = prop;
      this.sortOrder = order;
      this.fetchAdmins(1);
    },
    handlePageSizeChange(newSize) {
      this.pagination.perPage = newSize;
      this.pagination.currentPage = 1;
      this.fetchAdmins(1);
    },
    getAdmin() {
      return adminUsersService
        .list()
        .then((admins) => {
          this.admins = admins;
        })
        .catch((error) => {
          this.$notifyError("Could not load admins: " + error);
        });
    },
    getOffices() {
      return officesService
        .list()
        .then((offices) => {
          this.officeOptions = offices.map((office) => ({
            id: office.id,
            name: office.name,
          }));
        })
        .catch((error) => {
          this.$notifyError("Could not load offices: " + error);
        });
    },
    arePasswordsSame() {
      return this.admin.password === this.admin.confirmPassword;
    },
    validatePassword(password) {
      // At least 8 characters
      if (password.length < 8) {
        return { valid: false, message: "Password must be at least 8 characters long." };
      }
      // At least one number
      if (!/\d/.test(password)) {
        return { valid: false, message: "Password must contain at least one number." };
      }
      // At least one special character
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        return { valid: false, message: "Password must contain at least one special character." };
      }
      return { valid: true };
    },
    toggleAdminStatus(admin) {
      const isActive = admin.active === 1;
      const newStatus = isActive ? 0 : 1;
      const action = isActive ? "deactivated" : "activated";

      return adminUsersService
        .update(admin.id, { active: newStatus })
        .then(() => {
          this.$notifySuccess(`Successfully ${action} admin!`);
          this.fetchAdmins(this.pagination.currentPage);
        })
        .catch((error) => {
          this.$notifyError(`Error ${action.slice(0, -1)}ing admin: ${error}`);
        });
    },
    async saveAdmin() {
      const adminObj = this.admin;

      if (!adminObj.first_name) {
        this.$notifyError("No admin first name!");
        return;
      }
      if (!adminObj.last_name) {
        this.$notifyError("No admin last name!");
        return;
      }
      if (!adminObj.email) {
        this.$notifyError("No admin email!");
        return;
      }
      if (!this.adminBeingEdited) {
        if (!adminObj.password) {
          this.$notifyError("No admin password!");
          return;
        }
        if (!this.arePasswordsSame()) {
          this.$notifyError("Passwords do not match.");
          return;
        }
        // Validate password requirements
        const passwordValidation = this.validatePassword(adminObj.password);
        if (!passwordValidation.valid) {
          this.$notifyError(passwordValidation.message);
          return;
        }
      }

      this.$spinner.show();
      let apiSaveCall;

      if (this.adminBeingEdited) {
        const { password, confirmPassword, ...payloadWithoutPassword } = adminObj;
        payloadWithoutPassword.user_type = "Admin";
        apiSaveCall = adminUsersService
          .update(adminObj.id, payloadWithoutPassword)
          .then(() => {
            this.$notifySuccess("Successfully updated admin.");
          });
      } else {
        const { confirmPassword, ...adminPayload } = adminObj;
        adminPayload.user_type = "Admin";
        apiSaveCall = adminUsersService.create(adminPayload).then(() => {
          this.$notifySuccess("Successfully created admin.");
        });
      }
      apiSaveCall
        .then(() => {
          // Only close modal and refresh data on success
          this.$modal.close("create_or_edit_admin");
          this.adminBeingEdited = null;
          return this.fetchAdmins();
        })
        .catch((error) => {
          // Extract the error message from the API response if available
          const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || error;
          this.$notifyError("Error: " + errorMessage);
          // Modal stays open so user can correct the error
        })
        .finally(() => {
          this.$spinner.hide();
        });
    },
    createAdmin() {
      this.adminBeingEdited = null;
      this.newAdmin = {
        first_name: "",
        last_name: "",
        email: "",
        mobile_phone: "",
        other_phone: "",
        password: "",
        confirmPassword: "",
        title: "",
        business_unit: "MNG - Management",
        position_id: null,
        office_ids: [],
        address: {
          address1: "",
          address2: "",
          city: "",
          state: "",
          zip: "",
        },
      };
      this.$modal.open("create_or_edit_admin");
    },
    async editAdmin(admin) {
      this.adminBeingEdited = {
        ...admin,
        business_unit: admin.business_unit || "MNG - Management",
        office_ids: admin.managed_offices ? admin.managed_offices.map(office => office.id) : [],
      };

      this.$modal.open("create_or_edit_admin");
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },
    toggleConfirmPasswordVisibility() {
      this.confirmPasswordVisible = !this.confirmPasswordVisible;
    },
  },
};
</script>

<style scoped>
/* Admin Form Container with Scrolling */
.admin-form-container {
  max-height: calc(85vh - 180px);
  overflow-y: auto;
  padding: 20px;
}

/* Dialog body overflow handling */
:deep(.el-dialog__body) {
  overflow: hidden !important;
  padding: 0 !important;
}

/* Password styling to match AIVRow */
.password-input-wrapper {
  position: relative;
}

.password-toggle-icon {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #ffffff !important;
  z-index: 10;
  font-size: 16px;
}

.password-toggle-icon:hover {
  color: #ffffff !important;
}

.password-requirements {
  font-size: 0.9em;
  color: grey;
  margin-top: 8px;
}

.password-requirements ul {
  margin-bottom: 0;
  padding-left: 1.2rem;
}

/* Match AIVRow label styling */
label.d-block {
  margin-bottom: 2px !important;
}

@media (max-width: 768px) {
  .password-row .col-md-4 {
    margin-bottom: 15px;
  }
}

/* Compact filter section */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
}

.filter-group {
  display: flex;
  align-items: center;
  min-width: 0;
  flex-shrink: 0;
}

.filter-group.search-group {
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
}

.compact-filter {
  min-width: 150px;
  width: 150px;
}

@media (max-width: 768px) {
  .filter-row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .filter-group.search-group {
    max-width: unset;
  }

  .compact-filter {
    width: 100%;
    min-width: unset;
  }
}
</style>
